import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Button, Box, CircularProgress, Alert, Collapse, IconButton, TextField, Link
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../components/AuthProvider';
import { useNavigate } from 'react-router-dom';
import AdminOrderItemRow from '../components/OrderItemRow';

interface Order {
  id: number;
  customer_name: string;
  email: string;
  address: string;
  total_price: number;
  status: string;
  stripe_payment_id: string;
  date_received: string;
  date_completed: string;
  tracking_number?: string;
  date_shipped?: string;
  status_token: string;
}

interface OrderItem {
  id: number;
  order_id: number;
  product_id: number;
  variation_id: number;
  name: string;
  quantity: number;
  price: number;
  custom_details: string;
}

const API_BASE = process.env.REACT_APP_API_URL;

const AdminOrders: React.FC = () => {
  const { isAdmin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
  const [orderItems, setOrderItems] = useState<{ [key: number]: OrderItem[] }>({});
  const [trackingInputs, setTrackingInputs] = useState<{ [key: number]: string }>({});

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      navigate('/login');
      return;
    }

    const fetchOrders = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/orders`);
        if (!response.ok) {
          throw new Error('Failed to fetch orders');
        }
        const data = await response.json();
        setOrders(data);
      } catch (err: any) {
        setError(err.message || 'Error fetching orders');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated, isAdmin, navigate]);

  const fetchOrderItems = async (orderId: number) => {
    if (orderItems[orderId]) {
      setExpandedOrder(expandedOrder === orderId ? null : orderId);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/orders/${orderId}/items`);
      if (!response.ok) throw new Error('Failed to fetch order items');

      const data = await response.json();

      setOrderItems((prev) => ({ ...prev, [orderId]: data }));
      setExpandedOrder(orderId);
    } catch (err: any) {
      setError(err.message || 'Error fetching order items');
    }
  };

  const markAsCompleted = async (orderId: number) => {
    try {
      const response = await fetch(`${API_BASE}/api/orders/${orderId}`, { method: 'PUT' });
      if (!response.ok) throw new Error('Failed to update order');

      //const updatedOrder = await response.json();

      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId
            ? { ...order, status: 'Completed', date_completed: new Date().toISOString() }
            : order
        )
      );
    } catch (err: any) {
      setError(err.message || 'Error updating order');
    }
  };

  const markAsShipped = async (orderId: number) => {
    const trackingNumber = trackingInputs[orderId];
    if (!trackingNumber) {
      alert('Please enter a tracking number.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/orders/${orderId}/ship`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trackingNumber }),
      });

      if (!response.ok) throw new Error('Failed to mark as shipped');

      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId
            ? {
                ...order,
                status: 'Shipped',
                tracking_number: trackingNumber,
                date_shipped: new Date().toISOString(),
              }
            : order
        )
      );
    } catch (err: any) {
      setError(err.message || 'Error updating shipment');
    }
  };

  if (!isAuthenticated || !isAdmin) return null;

  return (
    <Container maxWidth="lg" sx={{mt:5}}>
      
      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>#</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Stripe Payment ID</TableCell>
                <TableCell>Date Received</TableCell>
                <TableCell>Date Completed</TableCell>
                <TableCell>Tracking #</TableCell>
                <TableCell>Date Shipped</TableCell>
                <TableCell>Status Link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <React.Fragment key={order.id}>
                  <TableRow>
                    <TableCell>
                      <IconButton onClick={() => fetchOrderItems(order.id)} size="small">
                        <ExpandMoreIcon
                          sx={{
                            transform: expandedOrder === order.id ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                          }}
                        />
                      </IconButton>
                    </TableCell>
                    <TableCell>{order.id}</TableCell>
                    <TableCell>{order.status}</TableCell>
                    <TableCell>{order.customer_name}</TableCell>
                    <TableCell>{order.email}</TableCell>
                    <TableCell>{order.address}</TableCell>
                    <TableCell>${order.total_price.toFixed(2)}</TableCell>
                    <TableCell>{order.stripe_payment_id || 'N/A'}</TableCell>
                    <TableCell>{order.date_received}</TableCell>
                    <TableCell>{order.date_completed || '—'}</TableCell>
                    <TableCell>{order.tracking_number || '—'}</TableCell>
                    <TableCell>{order.date_shipped || '—'}</TableCell>
                    <TableCell>
                    {order.status_token ? 
                    <Link
                      href={`/order-status/${order.status_token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >{order.status_token}</Link> : 
                    "no token"
                    }
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                      <Collapse in={expandedOrder === order.id} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
                            Order Items
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Product Name</TableCell>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Subtotal</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {orderItems[order.id]?.map((item) => (
                                <AdminOrderItemRow key={item.id} item={item} />
                              ))}
                            </TableBody>
                          </Table>
                          <Box display="flex" alignItems="center" gap={2} mt={3}>
                            <TextField
                              label="Tracking Number"
                              size="small"
                              value={trackingInputs[order.id] || ''}
                              onChange={(e) =>
                                setTrackingInputs((prev) => ({ ...prev, [order.id]: e.target.value }))
                              }
                              disabled={order.status === 'Shipped' || order.status === 'Completed'}
                            />
                            {order.status !== 'Shipped' && order.status !== 'Completed' && (
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => markAsShipped(order.id)}
                              >
                                Mark as Shipped
                              </Button>
                            )}
                            {order.status !== 'Completed' && (
                              <Button
                                variant="contained"
                                color="success"
                                onClick={() => markAsCompleted(order.id)}
                              >
                                Mark as Completed
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Container>
  );
};

export default AdminOrders;
